import React from 'react';
import { Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import dayjs from 'dayjs';
import { useAppUi } from '../app/useAppUi';
import { rangeForDays } from '../utils/dateRange';

const PRESETS = [
  { key: 'today', days: 1 },
  { key: 'd7', days: 7 },
  { key: 'd30', days: 30 },
  { key: 'd90', days: 90 },
];

// Open the calendar on a click anywhere in the field, not only on the small icon.
const openPicker = (e) => {
  try {
    e.currentTarget.showPicker?.();
  } catch {
    // Older browsers / not allowed here: the icon still works.
  }
};

const presetOf = ({ from, to }) =>
  PRESETS.find((p) => {
    const r = rangeForDays(p.days);
    return r.from === from && r.to === to;
  })?.key || null;

/** Quick presets plus a custom from–to pair. */
export function DateRangeBar({ value, onChange }) {
  const { t } = useAppUi();
  const today = dayjs().format('YYYY-MM-DD');
  const dateSx = { width: 150, '& input': { py: 0.75, fontSize: 13, cursor: 'pointer' } };

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={presetOf(value)}
        onChange={(_, key) => {
          const p = PRESETS.find((x) => x.key === key);
          if (p) onChange(rangeForDays(p.days));
        }}
      >
        {PRESETS.map((p) => (
          <ToggleButton key={p.key} value={p.key} sx={{ fontSize: 12.5, px: 1.5 }}>
            {t(`range.${p.key}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <TextField
        type="date"
        size="small"
        aria-label={t('range.from')}
        value={value.from}
        slotProps={{ htmlInput: { max: value.to, onClick: openPicker } }}
        onChange={(e) => e.target.value && onChange({ ...value, from: e.target.value })}
        sx={dateSx}
      />
      <TextField
        type="date"
        size="small"
        aria-label={t('range.to')}
        value={value.to}
        slotProps={{ htmlInput: { min: value.from, max: today, onClick: openPicker } }}
        onChange={(e) => e.target.value && onChange({ ...value, to: e.target.value })}
        sx={dateSx}
      />
    </Stack>
  );
}
